import React from "react";
import styled from "styles/styled_components";
import { partial } from "ramda";
import { Maybe } from "true-myth";

import { IOption } from "components/shared/dropdown";
import Icon, { ICON_COLOR, ICON_TYPE } from "components/shared/icon";
import THEME from "styles/theme";

export interface IDropdownOption<T> {
  isExpanded: boolean;
  isSelected: boolean;
  onOptionClick: (option: Maybe<IOption<T>>) => void;
  option: Maybe<IOption<T>>;
  createOptionRef: (element: HTMLLIElement) => void;
}

interface IStyledOptionProps {
  isSelected: boolean;
  isExpanded: boolean;
}

const StyledOption = styled("li")<IStyledOptionProps>`
  align-items: center;
  background-color: ${(props) => props.theme.colors.brandWhite};
  border-radius: ${(props) => props.theme.borderRadius};
  cursor: pointer;
  display: flex;
  flex-direction: row;
  height: ${(props) => `calc(4 * ${props.theme.gridBase})`};
  justify-content: space-between;
  padding: ${(props) =>
    `${props.theme.gridBase} calc(2 * ${props.theme.gridBase})`};

  :hover {
    background-color: ${(props) =>
      props.isExpanded ? props.theme.colors.brandBlue10 : ""};
  }
`;

function DropdownOption<T>({
  option,
  onOptionClick,
  isSelected,
  isExpanded,
  createOptionRef,
}: IDropdownOption<T>) {
  const unwrappedLabel = option.map((opt) => opt.label).unwrapOr("");

  let selectionIcon;
  if (isSelected && !isExpanded) {
    selectionIcon = (
      <Icon
        color={ICON_COLOR.GRAY}
        type={ICON_TYPE.CHEVRON_DOWN}
        size={THEME.iconSize.small}
      />
    );
  } else if (isSelected && isExpanded) {
    selectionIcon = (
      <Icon
        color={ICON_COLOR.GRAY}
        type={ICON_TYPE.CHECKMARK}
        size={THEME.iconSize.tiny}
      />
    );
  }

  return (
    <StyledOption
      ref={isSelected ? createOptionRef : null}
      role="option"
      isExpanded={isExpanded}
      isSelected={isSelected}
      aria-selected={isSelected}
      onClick={partial(onOptionClick, [option])}
    >
      {unwrappedLabel}
      {selectionIcon}
    </StyledOption>
  );
}

export default DropdownOption;
